import { useEffect, useState } from 'react'
import { OrderFinanceDrawerFormProps } from './order-finance-drawer-form.type'
import { Button, DrawerStepper } from '@mercai/clever'
import { SaleOrderProps } from '@/types'
import { useTranslation } from 'react-i18next'
import { QUERIES_ADMIN, useOrderCenterSaleOrderUpdate } from '@/services'
import { onErrorMessage, onSuccessMessage } from '@/helpers'
import { useQueryClient } from '@tanstack/react-query'
import { getFormattedPayloadData } from './order-finance-drawer-form.constants'
import { FinanceForm } from '@/components'

export const OrderFinanceDrawerForm = ({
  isOpen,
  initialValues,
  onHandleClose,
  initialTab = 'invoices',
}: OrderFinanceDrawerFormProps) => {
  const { t } = useTranslation('screenOrderCenterPage')

  const queryClient = useQueryClient()

  const [values, setValues] = useState(initialValues as SaleOrderProps)

  const { mutateAsync } = useOrderCenterSaleOrderUpdate(
    initialValues?.uuid || '',
  )

  const onSucess = () => {
    queryClient.invalidateQueries({
      queryKey: [QUERIES_ADMIN.ORDER_CENTER.module, initialValues?.uuid],
    })
  }

  const onSubmit = async () => {
    const response = await mutateAsync(getFormattedPayloadData(values))

    if (response.success) {
      onSucess()

      onSuccessMessage(
        t('orderCenterShowTabProductsSuccessMessageTitle'),
        t('orderCenterShowTabProductsSuccessMessageDescription'),
      )

      onHandleClose()
    } else {
      onErrorMessage(
        t('orderCenterShowTabProductsErrorMessageTitle'),
        t('orderCenterShowTabProductsErrorMessageDescription'),
      )
    }
  }

  useEffect(() => {
    if (isOpen) {
      setValues(initialValues as SaleOrderProps)
    } else {
      setValues({} as SaleOrderProps)
    }
  }, [isOpen])

  return (
    <DrawerStepper
      isOpen={isOpen}
      onHandleOpen={onHandleClose}
      initialStep="root"
      translations={{ optional: '' }}
      title={t('orderCenterShowTabClientFinanceDrawerFormTitle', {
        reference: initialValues.reference,
      })}
      description={t('orderCenterShowTabClientFinanceDrawerFormDescription')}
      steps={{
        root: {
          backNavigate: 'root',
          isOptional: false,
          component: () => (
            <FinanceForm
              initialTab={initialTab}
              values={values}
              onChange={(values) => setValues(values)}
              onSuccessUploadFiles={onSucess}
            />
          ),
          footer: () => (
            <>
              <Button variant="secondary" onClick={onHandleClose}>
                {t('formTransactionStepFormBack')}
              </Button>
              <Button variant="primary" onClick={onSubmit}>
                {t('formTransactionStepFormSubmit')}
              </Button>
            </>
          ),
        },
      }}
    />
  )
}
